import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Shield, ChevronDown, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTheme } from '../../contexts/ThemeContext';
type ForwardTarget = 'vexu' | 'other' | 'disabled';
interface ForwardingScenario {
  id: string;
  name: string;
  icon: React.ElementType;
  status: ForwardTarget;
  otherNumber?: string;
}
interface CallForwardingCardProps {
  syncPhase: 'idle' | 'confirm' | 'scanning' | 'synced';
  scenarios: ForwardingScenario[];
  forwardingExpanded: boolean;
  setForwardingExpanded: (expanded: boolean) => void;
  isActivated: boolean;
  startSync: () => void;
  handleDisable: (scenario: ForwardingScenario) => void;
  handleEnable: (scenario: ForwardingScenario) => void;
  onNavigate: (screen: string) => void;
  platform: 'ios' | 'android';
  onTogglePlatform: () => void;
}
export function CallForwardingCard({
  syncPhase,
  scenarios,
  forwardingExpanded,
  setForwardingExpanded,
  isActivated,
  startSync,
  handleDisable,
  handleEnable,
  onNavigate,
  platform,
  onTogglePlatform
}: CallForwardingCardProps) {
  const {
    isLight
  } = useTheme();
  const renderScenarioStatus = (scenario: ForwardingScenario) => {
    if (scenario.status === 'vexu') {
      return <div className="flex items-center gap-1.5" data-id="element-166">
          <CheckCircle2 className={`w-3.5 h-3.5 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-167" />
          <span className={`text-[10px] font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-168">
            Forwarded to Vexu AI
          </span>
          <button onClick={() => handleDisable(scenario)} className={`ml-2 text-[10px] font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-red-500' : 'text-zinc-500 hover:text-red-400'}`} data-id="element-169">
            Disable
          </button>
        </div>;
    }
    if (scenario.status === 'other') {
      return <div className="flex items-center gap-1.5" data-id="element-170">
          <AlertTriangle className={`w-3.5 h-3.5 ${isLight ? 'text-amber-500' : 'text-amber-400'}`} data-id="element-171" />
          <span className={`text-[10px] font-medium ${isLight ? 'text-amber-600' : 'text-amber-400'}`} data-id="element-172">
            Forwarded to {scenario.otherNumber}
          </span>
          <button onClick={() => handleEnable(scenario)} className={`ml-2 text-[10px] font-semibold transition-colors ${isLight ? 'text-vexu-600 hover:text-vexu-700' : 'text-vexu hover:text-vexu-400'}`} data-id="element-173">
            Switch to Vexu
          </button>
        </div>;
    }
    return <div className="flex items-center gap-1.5" data-id="element-174">
        <span className={`text-[10px] font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-175">
          Not forwarded
        </span>
        <button onClick={() => handleEnable(scenario)} className={`text-[10px] font-semibold transition-colors ${isLight ? 'text-vexu-600 hover:text-vexu-700' : 'text-vexu hover:text-vexu-400'}`} data-id="element-176">
          Enable Vexu.ai
        </button>
      </div>;
  };
  return <div className="space-y-3" data-id="element-177">
      <div className="flex items-center justify-between" data-id="element-178">
        <h2 className={`text-sm font-bold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-179">
          Call Forwarding
        </h2>

        {/* Platform Toggle */}
        <div className={`flex items-center p-0.5 rounded-full border ${isLight ? 'bg-slate-100 border-slate-200' : 'bg-white/5 border-white/10'}`} data-id="element-180">
          <button onClick={platform === 'android' ? onTogglePlatform : undefined} className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${platform === 'ios' ? isLight ? 'bg-white text-slate-900 shadow-sm' : 'bg-[#222] text-white shadow-sm' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-181">
            iOS
          </button>
          <button onClick={platform === 'ios' ? onTogglePlatform : undefined} className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${platform === 'android' ? isLight ? 'bg-white text-slate-900 shadow-sm' : 'bg-[#222] text-white shadow-sm' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-182">
            Android
          </button>
        </div>
      </div>

      <div className={`rounded-[24px] overflow-hidden transition-colors duration-500 ${isLight ? 'bg-white/80 border border-slate-200/60 shadow-sm backdrop-blur-xl' : 'bg-[#111111]/80 border border-white/5 backdrop-blur-xl'}`} data-id="element-183">
        <AnimatePresence mode="wait" data-id="element-184">
          {syncPhase === 'idle' && <motion.div key="idle" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="p-6 flex flex-col items-center text-center" data-id="element-185">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-186">
                <RefreshCw className={`w-8 h-8 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-187" />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-188">
                Check Carrier Settings
              </h3>
              <p className={`text-sm mb-6 max-w-[240px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-189">
                We need to check your carrier settings to see if call forwarding
                is active.
              </p>
              <Button onClick={startSync} fullWidth data-id="element-190">
                Sync Carrier Settings
              </Button>
            </motion.div>}

          {syncPhase === 'synced' && !isActivated && <motion.div key="activate" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="p-6 flex flex-col items-center text-center" data-id="element-191">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-5 ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-192">
                <Shield className={`w-8 h-8 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-193" />
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-194">
                Activate Vexu.ai
              </h3>
              <p className={`text-sm mb-2 max-w-[260px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-195">
                Start protecting your missed calls with AI. Vexu.ai can answer
                when you're busy, unreachable, or your phone is off.
              </p>
              <div className="flex flex-col w-full mt-4 gap-3" data-id="element-196">
                <div className={`rounded-xl p-3 ${isLight ? 'bg-slate-50' : 'bg-white/5'}`} data-id="element-197">
                  <div className="flex items-center justify-between" data-id="element-198">
                    <span className={`text-xs font-medium ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-199">
                      Forwarding conditions
                    </span>
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-200">
                      0 of 4 active
                    </span>
                  </div>
                </div>
                <Button onClick={() => onNavigate('planSelection')} fullWidth data-id="element-201">
                  Activate Now
                </Button>
              </div>
            </motion.div>}

          {syncPhase === 'synced' && isActivated && <motion.div key="synced" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-202">
              {/* Collapsed summary — always visible */}
              <button onClick={() => setForwardingExpanded(!forwardingExpanded)} className="w-full text-left px-5 py-4 flex items-center justify-between" data-id="element-203">
                <div className="flex items-center gap-3" data-id="element-204">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-205">
                    <Shield className={`w-4.5 h-4.5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-206" />
                  </div>
                  <div data-id="element-207">
                    <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-208">
                      {scenarios.filter(s => s.status === 'vexu').length} of 4
                      Forwarded
                    </p>
                    <p className={`text-[10px] font-medium mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-209">
                      AI answers your missed calls
                    </p>
                  </div>
                </div>
                <motion.div animate={{
              rotate: forwardingExpanded ? 180 : 0
            }} transition={{
              duration: 0.2
            }} data-id="element-210">
                  <ChevronDown className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-211" />
                </motion.div>
              </button>

              {/* Expanded details */}
              <AnimatePresence data-id="element-212">
                {forwardingExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-213">
                    <div className={`${isLight ? 'border-t border-slate-100' : 'border-t border-white/5'}`} data-id="element-214">
                      {scenarios.map((scenario, i) => {
                  const Icon = scenario.icon;
                  return <div key={scenario.id} className={`flex items-center justify-between p-4 ${i !== scenarios.length - 1 ? isLight ? 'border-b border-slate-100' : 'border-b border-white/5' : ''}`} data-id="element-215">
                            <div className="flex items-center gap-3" data-id="element-216">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${scenario.status === 'vexu' ? isLight ? 'bg-vexu/10' : 'bg-vexu/10' : isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-217">
                                <Icon className={`w-4 h-4 ${scenario.status === 'vexu' ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-218" />
                              </div>
                              <div data-id="element-219">
                                <span className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-220">
                                  {scenario.name}
                                </span>
                                <div className="mt-0.5" data-id="element-221">
                                  {renderScenarioStatus(scenario)}
                                </div>
                              </div>
                            </div>
                          </div>;
                })}
                      <div className={`p-3 flex justify-center ${isLight ? 'bg-slate-50 border-t border-slate-100' : 'bg-white/[0.02] border-t border-white/5'}`} data-id="element-222">
                        <button onClick={startSync} className={`font-medium flex items-center gap-1 transition-colors ${isLight ? 'text-vexu-600 hover:text-vexu-700' : 'text-vexu hover:text-vexu-400'}`} data-id="element-223">
                          <RefreshCw className="w-3 h-3" data-id="element-224" /> Refresh
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}